// /finance/documents — Wave 75: TH hub of financial & banking document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/finance/documents";
const URL = `${SITE}${PATH}`;
const EN_URL = `${SITE}/en/finance/documents`;
const TITLE = "เอกสารการเงินและธนาคาร — 6 กรณี | NYC";
const DESC =
  "เอกสารการเงินและธนาคาร 6 กรณี รายการเดินบัญชี หนังสือรับรองยอดเงินฝาก หนังสือรับรองเงินเดือน หนังสือสนับสนุนค่าใช้จ่าย เอกสารสินเชื่อและจำนอง และหลักฐานที่มาของเงินโอนข้ามประเทศ";

export const Route = createFileRoute("/finance/documents/")({
  loader: async () => {
    const { FINANCE_DOC_GROUPS, FINANCE_APOSTILLE_TH, FINANCE_AUTHORITY_TH } = await import("@/data/finance-doc-groups");
    return {
      __d: { FINANCE_DOC_GROUPS, FINANCE_APOSTILLE_TH, FINANCE_AUTHORITY_TH },
      groups: FINANCE_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleTh,
        faqs: g.faqsTh.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "th") },
      { name: "description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "th") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: URL },
      { rel: "alternate", hrefLang: "en", href: EN_URL },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "th",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "กรณีเอกสารการเงินและธนาคาร",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: FinanceDocumentsPage,
});

function FinanceDocumentsPage() {
  const { __d: { FINANCE_DOC_GROUPS, FINANCE_APOSTILLE_TH, FINANCE_AUTHORITY_TH } } = Route.useLoaderData() as { __d: typeof import("@/data/finance-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">เอกสารการเงิน / กรณีทั้งหมด</nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">เอกสารการเงินและธนาคาร — 6 กรณี</h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        เอกสารการเงินแยกเส้นทางตามว่าใครเป็นผู้ออกเอกสาร ธนาคาร นายจ้าง หรือตัวผู้ยื่นเอง
        เพราะผู้ออกเอกสารเป็นตัวกำหนดว่าต้องรับรองลายมือชื่อหรือไม่ และต้องผ่านลำดับการรับรองใดก่อนใช้ในต่างประเทศ
        หน้านี้แบ่งงานออกเป็น 6 กรณี ระบุเอกสารที่ต้องเตรียม ลำดับขั้นตอน เช็กลิสต์ คำแนะนำ และข้อควรระวังของแต่ละกรณี
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{FINANCE_AUTHORITY_TH}</aside>
      <aside className="mt-3 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{FINANCE_APOSTILLE_TH}</aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {FINANCE_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link to="/finance/documents/$group" params={{ group: g.slug }} className="text-primary hover:underline">
              {g.titleTh}
            </Link>
          </li>
        ))}
      </ul>

      {FINANCE_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleTh}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryTh}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyTh.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/finance/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            อ่านคู่มือฉบับเต็ม: {g.titleTh} →
          </Link>
        </section>
      ))}

      <p className="mt-10 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        ส่งสแกนเอกสารทาง LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd
        ทีมงานจะแจ้งขั้นตอน เอกสารที่ยังขาด และกรอบเวลาที่เป็นไปได้จริงตามกรณีของคุณ
      </p>
          <ServiceAuthorityBlock topic="finance" locale="th" />
    </main>
  );
}
